import { create } from 'zustand';
import { getStaffPricing, type StaffPricingResponse } from '../api/pricing.js';

interface PricingState {
  data: StaffPricingResponse | null;
  loadedAt: number | null;
  loading: boolean;
  /** True si el backend devolvió 403 (no es staff) → no reintentar. */
  forbidden: boolean;

  /** Carga si no se cargó hace poco (<5 min). Silenciosa. */
  ensureLoaded: () => Promise<void>;
  refresh: () => Promise<void>;
  clear: () => void;
}

const FRESH_MS = 5 * 60 * 1000;

export const usePricingStore = create<PricingState>((set, get) => ({
  data: null,
  loadedAt: null,
  loading: false,
  forbidden: false,

  ensureLoaded: async () => {
    const s = get();
    if (s.forbidden || s.loading) return;
    if (s.loadedAt && Date.now() - s.loadedAt < FRESH_MS) return;
    await get().refresh();
  },

  refresh: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const data = await getStaffPricing();
      set({ data, loadedAt: Date.now(), loading: false });
    } catch (err) {
      // 403 = no es staff: marcar y no reintentar (es esperable para clientes).
      const status = (err as { status?: number }).status;
      set({ loading: false, forbidden: status === 403 });
    }
  },

  clear: () => set({ data: null, loadedAt: null, loading: false, forbidden: false }),
}));

/** Tarifa USD/1k de un modelo desde el cache (o null si no está cargado). */
export function modelRatePer1k(
  data: StaffPricingResponse | null,
  model: string,
): { in: number; out: number } | null {
  const row = data?.models.find((m) => m.model === model);
  if (!row) return null;
  return { in: Number(row.in_per_1k), out: Number(row.out_per_1k) };
}
